import axios from "axios";
import JSZip from "jszip";
import { saveAs } from "file-saver";

export interface DownloadQueueItem {
  id: string;
  fileName: string;
  progress: number; // 0-1
  status: "downloading" | "done" | "error" | "cancelled";
  errorMessage?: string;
  etaSeconds?: number;
}

type Listener = () => void;

// simple pub-sub store, module-level singleton so downloads survive component
// unmounts caused by in-app navigation, mirrors uploadQueueStore.ts
class DownloadQueueStore {
  private items: DownloadQueueItem[] = [];
  private listeners: Listener[] = [];
  private controllers: Map<string, AbortController> = new Map();

  getItems(): DownloadQueueItem[] {
    return this.items;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  private notify() {
    this.listeners.forEach((l) => l());
  }

  private patch(id: string, changes: Partial<DownloadQueueItem>) {
    this.items = this.items.map((it) =>
      it.id === id ? { ...it, ...changes } : it,
    );
    this.notify();
  }

  clearCompleted() {
    this.items = this.items.filter((it) => it.status === "downloading");
    this.notify();
  }

  cancel(id: string) {
    const controller = this.controllers.get(id);
    if (controller) {
      controller.abort();
    }
  }

  cancelAll() {
    this.items
      .filter((it) => it.status === "downloading")
      .forEach((it) => this.cancel(it.id));
  }

  // wipes the queue, e.g. on logout, so the widget doesn't resurface stale
  // items from a previous session when DownloadManager remounts on re-login
  reset() {
    this.cancelAll();
    this.controllers.clear();
    this.items = [];
    this.notify();
  }

  // fetches a blob for a queue item that already exists, updating its
  // progress/ETA as it streams - does not save the file, callers decide
  // whether/how the resulting blob is persisted
  private fetchTracked(
    id: string,
    url: string,
    expectedBytes?: number,
  ): Promise<Blob> {
    const controller = new AbortController();
    this.controllers.set(id, controller);
    const startTime = Date.now();

    return axios
      .get(url, {
        responseType: "blob",
        signal: controller.signal,
        onDownloadProgress: (progressEvent) => {
          const total = progressEvent.total || expectedBytes;
          if (total) {
            const elapsedSeconds = (Date.now() - startTime) / 1000;
            // skip ETA on the very first ticks - elapsedSeconds near 0 makes
            // the rate spike and reports a near-0, meaningless time estimate
            let etaSeconds: number | undefined;
            if (elapsedSeconds > 0.5) {
              const rate = progressEvent.loaded / elapsedSeconds;
              if (rate > 0) {
                etaSeconds = (total - progressEvent.loaded) / rate;
              }
            }
            this.patch(id, {
              progress: Math.min(progressEvent.loaded / total, 1),
              etaSeconds,
            });
          }
        },
      })
      .then((res) => {
        this.controllers.delete(id);
        return res.data as Blob;
      })
      .catch((err) => {
        this.controllers.delete(id);
        if (
          axios.isCancel(err) ||
          err.name === "AbortError" ||
          err.name === "CanceledError"
        ) {
          this.patch(id, {
            status: "cancelled",
            errorMessage: "Download cancelled",
          });
        } else {
          this.patch(id, { status: "error", errorMessage: "Download failed" });
        }
        throw err;
      });
  }

  // expectedBytes is a fallback total for progress/ETA when the response has
  // no Content-Length header (e.g. chunked/streamed responses)
  download(url: string, fileName: string, expectedBytes?: number): Promise<void> {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    this.items = [
      ...this.items,
      { id, fileName, progress: 0, status: "downloading" },
    ];
    this.notify();

    return this.fetchTracked(id, url, expectedBytes).then((blob) => {
      saveAs(blob, fileName);
      this.patch(id, { progress: 1, status: "done" });
    });
  }

  // downloads several files in parallel - each shown as its own item in the
  // widget with real progress/ETA - and once ALL of them have completed,
  // bundles them into a single zip and saves that instead of the individual files
  downloadAllThenZip(
    files: { url: string; fileName: string; sizeBytes: number }[],
    zipFileName: string,
  ): Promise<void> {
    const entries = files.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file,
    }));
    this.items = [
      ...this.items,
      ...entries.map(({ id, file }) => ({
        id,
        fileName: file.fileName,
        progress: 0,
        status: "downloading" as const,
      })),
    ];
    this.notify();

    return Promise.all(
      entries.map(({ id, file }) =>
        this.fetchTracked(id, file.url, file.sizeBytes).then((blob) => {
          this.patch(id, { progress: 1, status: "done" });
          return blob;
        }),
      ),
    ).then((blobs) => {
      const zip = new JSZip();
      files.forEach((file, index) => {
        zip.file(file.fileName, blobs[index], { binary: true });
      });
      return zip.generateAsync({ type: "blob" }).then((content) => {
        saveAs(content, zipFileName);
      });
    });
  }
}

export const downloadQueueStore = new DownloadQueueStore();
